import { useState, useMemo } from 'react';
import { useFinanceStore } from '../store/useFinanceStore';
import { ChevronDown } from 'lucide-react';

interface CategorySelectorProps {
  value: string | null | undefined; // category_id selecionado
  onChange: (categoryId: string, categoryName: string) => void;
  type?: 'income' | 'expense'; // Filtrar categorias por tipo se necessário
  className?: string;
  appearance?: 'light' | 'dark';
}

export function CategorySelector({ value, onChange, type, className = '', appearance = 'dark' }: CategorySelectorProps) {
  const { categoriesWithSubs } = useFinanceStore();
  const [selectedMainId, setSelectedMainId] = useState<string | null>(null);
  const [isMainOpen, setIsMainOpen] = useState(false);
  const [isSubOpen, setIsSubOpen] = useState(false);
  const isDark = appearance === 'dark';
  const labelClass = isDark ? 'text-slate-300' : 'text-slate-600';
  const triggerClass = isDark
    ? 'bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-750'
    : 'bg-white border border-teal-100 text-slate-700 hover:bg-teal-50 shadow-sm';
  const dropdownClass = isDark
    ? 'bg-slate-800 border border-slate-700'
    : 'bg-white border border-teal-100 shadow-lg';
  const dropdownItemHover = isDark ? 'hover:bg-slate-700' : 'hover:bg-teal-50';
  const selectedTextClass = isDark ? 'text-slate-200' : 'text-slate-700';
  const placeholderClass = isDark ? 'text-slate-500' : 'text-slate-400';
  const badgeClass = isDark ? 'text-slate-500' : 'text-slate-500';
  const summaryCardClass = isDark
    ? 'bg-slate-800/50 text-slate-300 border border-slate-700/40'
    : 'bg-teal-50 text-teal-700 border border-teal-200';

  // Encontrar categoria selecionada atual
  const selectedCategory = useMemo(() => {
    if (!value) return null;
    
    for (const main of categoriesWithSubs) {
      if (main.id === value) return main;
      const sub = main.subcategories.find(s => s.id === value);
      if (sub) return sub;
    }
    return null;
  }, [value, categoriesWithSubs]);

  // Encontrar categoria principal da selecionada
  const selectedMainCategory = useMemo(() => {
    if (!value) return null;
    
    for (const main of categoriesWithSubs) {
      if (main.id === value) return main;
      if (main.subcategories.some(s => s.id === value)) return main;
    }
    return null;
  }, [value, categoriesWithSubs]);

  // Filtrar categorias por tipo se necessário
  const filteredMainCategories = useMemo(() => {
    // Por enquanto, todas as categorias são válidas para ambos
    // Futuramente pode filtrar: Freelance = income, resto = expense
    return categoriesWithSubs;
  }, [categoriesWithSubs, type]);

  const handleMainSelect = (mainCategory: typeof categoriesWithSubs[0]) => {
    setSelectedMainId(mainCategory.id);
    setIsMainOpen(false);
    
    // Se não tem subcategorias, seleciona direto a categoria principal
    if (mainCategory.subcategories.length === 0) {
      onChange(mainCategory.id, mainCategory.name);
    } else {
      setIsSubOpen(true);
    }
  };

  const handleSubSelect = (subCategory: typeof categoriesWithSubs[0]['subcategories'][0]) => {
    onChange(subCategory.id, subCategory.name);
    setIsSubOpen(false);
    setSelectedMainId(null);
  };

  const currentMainForSub = useMemo(() => {
    return categoriesWithSubs.find(c => c.id === selectedMainId);
  }, [selectedMainId, categoriesWithSubs]);

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Seletor de Categoria Principal */}
      <div className="relative">
        <label className={`block text-sm font-medium ${labelClass} mb-1`}>
          Categoria
        </label>
        <button
          type="button"
          onClick={() => setIsMainOpen(!isMainOpen)}
          className={`w-full px-4 py-3 rounded-lg text-left flex items-center justify-between transition-colors ${triggerClass}`}
        >
          <span className="flex items-center gap-2">
            {selectedMainCategory ? (
              <>
                <span className="text-xl">{selectedMainCategory.icon}</span>
                <span className={selectedTextClass}>{selectedMainCategory.name}</span>
              </>
            ) : (
              <span className={placeholderClass}>Selecione uma categoria...</span>
            )}
          </span>
          <ChevronDown className={`w-5 h-5 ${isDark ? 'text-slate-400' : 'text-teal-500'} transition-transform ${isMainOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Dropdown de categorias principais */}
        {isMainOpen && (
          <div className={`absolute z-50 w-full mt-1 rounded-lg max-h-64 overflow-y-auto ${dropdownClass}`}>
            {filteredMainCategories.map((category) => (
              <button
                key={category.id}
                type="button"
                onClick={() => handleMainSelect(category)}
                className={`w-full px-4 py-3 text-left transition-colors flex items-center gap-2 border-b last:border-b-0 ${dropdownItemHover} ${isDark ? 'border-slate-700' : 'border-teal-100'}`}
                style={{ borderLeftColor: category.color, borderLeftWidth: '4px' }}
              >
                <span className="text-xl">{category.icon}</span>
                <span className={selectedTextClass}>{category.name}</span>
                {category.subcategories.length > 0 && (
                  <span className={`ml-auto text-xs ${badgeClass}`}>
                    {category.subcategories.length} opções
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Seletor de Subcategoria (aparece após selecionar categoria principal) */}
      {currentMainForSub && currentMainForSub.subcategories.length > 0 && (
        <div className="relative">
          <label className={`block text-sm font-medium ${labelClass} mb-1`}>
            Subcategoria
          </label>
          <button
            type="button"
            onClick={() => setIsSubOpen(!isSubOpen)}
            className={`w-full px-4 py-3 rounded-lg text-left flex items-center justify-between transition-colors ${triggerClass}`}
          >
            <span className="flex items-center gap-2">
              {selectedCategory && selectedCategory.parent_id ? (
                <>
                  <span className="text-xl">{selectedCategory.icon}</span>
                  <span className={selectedTextClass}>{selectedCategory.name}</span>
                </>
              ) : (
                <span className={placeholderClass}>Selecione uma subcategoria...</span>
              )}
            </span>
            <ChevronDown className={`w-5 h-5 ${isDark ? 'text-slate-400' : 'text-teal-500'} transition-transform ${isSubOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown de subcategorias */}
          {isSubOpen && (
            <div className={`absolute z-50 w-full mt-1 rounded-lg max-h-64 overflow-y-auto ${dropdownClass}`}>
              {currentMainForSub.subcategories.map((subCategory) => (
                <button
                  key={subCategory.id}
                  type="button"
                  onClick={() => handleSubSelect(subCategory)}
                  className={`w-full px-4 py-3 text-left transition-colors flex items-center gap-2 border-b last:border-b-0 ${dropdownItemHover} ${isDark ? 'border-slate-700' : 'border-teal-100'}`}
                  style={{ borderLeftColor: subCategory.color, borderLeftWidth: '4px' }}
                >
                  <span className="text-xl">{subCategory.icon}</span>
                  <span className={selectedTextClass}>{subCategory.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Indicador visual da categoria completa selecionada */}
      {selectedCategory && (
        <div 
          className={`px-4 py-2 rounded-lg border-l-4 ${summaryCardClass}`}
          style={{ borderLeftColor: selectedCategory.color }}
        >
          <div className="flex items-center gap-2 text-sm">
            <span className="text-lg">{selectedCategory.icon}</span>
            <span className={isDark ? 'text-slate-400' : 'text-slate-600'}>
              {selectedMainCategory?.name}
              {selectedCategory.parent_id && ` → ${selectedCategory.name}`}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
